import { useEffect, useMemo, useState } from "react";

import type { ObjektkalkulationEingabe, ObjektkalkulationErgebnis, PositionEingabe, StoffEingabe } from "@repo/objektkalkulation-calc";

import { kalkDetail, useKalkulation } from "../../lib/kalk";
import { useFeder } from "../../lib/spring";
import { Blatt } from "./Blatt";
import { Ergebnisse, Kennzahl, OfflineHinweis } from "./BogenRechner";
import { AuswahlFeld, Schalter, TextFeld, ZahlFeld } from "./Feld";
import { SchienenAuswahl, type SchienenWahl } from "./SchienenAuswahl";
import { StoffAuswahl, type StoffVorlage } from "./StoffAuswahl";
import { Zahl } from "./Zahl";

type Vorlagen = { festpreisStoffe: (StoffEingabe & { gruppe: string })[] };

const HERSTELLER = [
  { wert: "decocity", label: "Decocity Luna" },
  { wert: "interstil", label: "Interstil" },
  { wert: "interstilMotor", label: "Interstil Motor" },
  { wert: "erfal", label: "Erfal" },
] as const;

const MONTAGE_STANDARD = { montagetage: 1, monteure: 2, nettoLohnProStunde: 40, margeProzent: 60, anfahrtBrutto: 45, handlingBruttoProDekometer: 0, inGruppenpreisen: true };

function neuePosition(stoffart: PositionEingabe["stoffart"]): PositionEingabe {
  return {
    apartment: "", zimmer: "", anzahl: 1, stoffart, dekolaengeCm: 200, bodenfreiheitCm: 1, vielfaches: 2, anzahlSchals: 2, hoeheUksCm: 250,
    schiene: { anzahl: 1, nischenmassCm: 200, ueberstandLinksCm: 0, ueberstandRechtsCm: 0, biegungen: 0, segmentboegen: 0, rundboegen: 0 },
  };
}

export function ObjektRechner() {
  const [eingabe, setEingabe] = useState<ObjektkalkulationEingabe | null>(null);
  const [vorlagen, setVorlagen] = useState<Vorlagen | null>(null);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [blattOffen, setBlattOffen] = useState(false);
  /** Welche Position gerade eine Schiene aus den Produkten wählt. */
  const [schieneFuer, setSchieneFuer] = useState<number | null>(null);

  useEffect(() => {
    kalkDetail<ObjektkalkulationEingabe>("objektkalkulation")
      .then((d) => {
        setEingabe(d.beispiel);
        setVorlagen((d.vorlagen as Vorlagen | undefined) ?? null);
      })
      .catch(() => setLadeFehler(true));
  }, []);

  const { ergebnis, fehler, offline } = useKalkulation<ObjektkalkulationErgebnis>("objektkalkulation", eingabe, eingabe != null);

  if (ladeFehler) return <OfflineHinweis />;
  if (!eingabe) return <p className="muted">Beispiel wird geladen …</p>;

  const setze = (patch: Partial<ObjektkalkulationEingabe>) => setEingabe((e) => (e ? { ...e, ...patch } : e));
  const setzePosition = (i: number, patch: Partial<PositionEingabe>) =>
    setEingabe((e) => (e ? { ...e, positionen: e.positionen.map((p, j) => (j === i ? { ...p, ...patch } : p)) } : e));

  /** Schiene aus dem Produkt: Preis je Hersteller-Rechner, Produkt als Herkunft, Hersteller der Kalkulation angleichen. */
  function uebernehmeSchiene(i: number, w: SchienenWahl) {
    const p = eingabe!.positionen[i]!;
    const basis = p.schiene ?? neuePosition(p.stoffart).schiene!;
    const aktuell = eingabe!.schiene?.hersteller ?? "decocity";
    const hersteller = w.rechner === "interstil" ? (aktuell === "interstilMotor" ? "interstilMotor" : "interstil") : "erfal";
    setEingabe((e) =>
      e
        ? {
            ...e,
            schiene: { ...e.schiene, hersteller },
            positionen: e.positionen.map((q, j) =>
              j === i ? { ...q, schiene: { ...basis, anzahl: Math.max(1, basis.anzahl), konfiguratorPreis: w.rechner === "interstil" ? w.bvk : w.nek, produkt: w.produkt } } : q,
            ),
          }
        : e,
    );
  }

  return (
    <div className="kalk-rechner kalk-rechner--breit">
      <section className="kalk-abschnitt">
        <div className="kalk-stoffe">
          <StoffKarte titel="Raumhoher Stoff" art="raumhoch" stoff={eingabe.stoffRaumhoch ?? null} vorlagen={vorlagen?.festpreisStoffe.filter((s) => s.bahnbreiteCm >= 260)} onChange={(s) => setze({ stoffRaumhoch: s })} />
          <StoffKarte titel="Bahnbreiter Stoff" art="bahnbreit" stoff={eingabe.stoffBahnbreit ?? null} vorlagen={vorlagen?.festpreisStoffe.filter((s) => s.bahnbreiteCm < 300)} onChange={(s) => setze({ stoffBahnbreit: s })} />
        </div>
        <div className="kalk-rechner__eingabe kalk-rechner__eingabe--zeile">
          <AuswahlFeld label="Schiene" wert={eingabe.schiene?.hersteller ?? "decocity"} onChange={(h) => setze({ schiene: { ...eingabe.schiene, hersteller: h } })} optionen={[...HERSTELLER]} />
          <Schalter label="Haken berechnen" an={eingabe.gleiter?.berechnen ?? false} onChange={(an) => setze({ gleiter: { ...eingabe.gleiter, berechnen: an } })} />
          <Schalter label="Montage" an={eingabe.montage != null} onChange={(an) => setze({ montage: an ? MONTAGE_STANDARD : null })} />
          {eingabe.montage && (
            <>
              <ZahlFeld label="Montagetage" wert={eingabe.montage.montagetage ?? 0} onChange={(v) => setze({ montage: { ...eingabe.montage!, montagetage: v, montagestunden: null } })} min={0} schritt={0.5} />
              <ZahlFeld label="Monteure" wert={eingabe.montage.monteure} onChange={(v) => setze({ montage: { ...eingabe.montage!, monteure: v } })} min={1} />
            </>
          )}
        </div>
      </section>

      <section className="kalk-abschnitt">
        <h2>Positionen</h2>
        <table className="kalk-tabelle kalk-tabelle--positionen">
          <thead>
            <tr>
              <th>App.</th><th>Zimmer</th><th>Stoff</th><th>Anz.</th><th>Dekolänge</th><th>Schals</th><th>Vielf.</th><th>Höhe UKS</th><th>Nische</th>
              <th className="num">Fertigbreite</th><th className="num kalk-spalte--nebensache">Stoff</th><th className="num kalk-spalte--nebensache">Nähen</th><th className="num">Summe VK</th><th aria-label="Aktion" />
            </tr>
          </thead>
          <tbody>
            {eingabe.positionen.map((p, i) => {
              const e = ergebnis?.positionen[i];
              const u = <span className="kalk-unsichtbar" />;
              return (
                <tr key={i} className={`kalk-zeile${e && !e.gueltig ? " kalk-zeile--fehler" : ""}`}>
                  <td><TextFeld label={u} wert={p.apartment ?? ""} onChange={(v) => setzePosition(i, { apartment: v })} platzhalter="App." /></td>
                  <td><TextFeld label={u} wert={p.zimmer ?? ""} onChange={(v) => setzePosition(i, { zimmer: v })} platzhalter="Zimmer" /></td>
                  <td><AuswahlFeld label={u} wert={p.stoffart} onChange={(v) => setzePosition(i, { stoffart: v })} optionen={[{ wert: "raumhoch", label: "raumhoch" }, { wert: "bahnbreit", label: "bahnbreit" }]} /></td>
                  <td><ZahlFeld label={u} wert={p.anzahl} onChange={(v) => setzePosition(i, { anzahl: v })} min={1} /></td>
                  <td><ZahlFeld label={u} wert={p.dekolaengeCm} onChange={(v) => setzePosition(i, { dekolaengeCm: v })} einheit="cm" min={1} schritt={5} /></td>
                  <td><ZahlFeld label={u} wert={p.anzahlSchals} onChange={(v) => setzePosition(i, { anzahlSchals: v })} min={1} /></td>
                  <td><ZahlFeld label={u} wert={p.vielfaches} onChange={(v) => setzePosition(i, { vielfaches: v })} min={1} schritt={0.5} /></td>
                  <td><ZahlFeld label={u} wert={p.hoeheUksCm} onChange={(v) => setzePosition(i, { hoeheUksCm: v })} einheit="cm" min={1} schritt={5} /></td>
                  <td>
                    <ZahlFeld label={u} wert={p.schiene?.nischenmassCm ?? 0} onChange={(v) => setzePosition(i, { schiene: { ...(p.schiene ?? neuePosition(p.stoffart).schiene!), nischenmassCm: v } })} einheit="cm" min={0} schritt={5} />
                    <button type="button" className={`kalk-produktknopf${p.schiene?.produkt ? " kalk-produktknopf--gesetzt" : ""}`} onClick={() => setSchieneFuer(i)} title={p.schiene?.produkt ? `${p.schiene.produkt.name} · ${p.schiene.konfiguratorPreis?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}` : "Schiene aus den Produkten wählen"}>
                      {p.schiene?.produkt ? p.schiene.produkt.name : "Produkt wählen"}
                    </button>
                  </td>
                  <td className="num">
                    {e?.masse ? (e.masse.fertigeBreiteCm != null ? <Zahl wert={e.masse.fertigeBreiteCm} art="cm" /> : <><Zahl wert={e.masse.fertigeBreiteBahnen ?? 0} /> Bahnen</>) : "–"}
                    {e?.masse?.gedreht && <small className="kalk-marke">gedreht</small>}
                  </td>
                  <td className="num kalk-spalte--nebensache">{e ? <Zahl wert={e.kosten.stoffmengeM} art="m" /> : "–"}</td>
                  <td className="num kalk-spalte--nebensache">{e ? <Zahl wert={e.kosten.naehVkNetto} art="euro" /> : "–"}</td>
                  <td className="num">{e ? <Zahl wert={e.kosten.summeVkNettoMitMontage} art="euro" /> : "–"}</td>
                  <td><button type="button" className="kalk-symbolknopf" aria-label="Position entfernen" disabled={eingabe.positionen.length === 1} onClick={() => setze({ positionen: eingabe.positionen.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="kalk-knoepfe">
          <button type="button" className="button" onClick={() => setze({ positionen: [...eingabe.positionen, neuePosition("raumhoch")] })}>+ Position raumhoch</button>
          <button type="button" className="button" onClick={() => setze({ positionen: [...eingabe.positionen, neuePosition("bahnbreit")] })}>+ Position bahnbreit</button>
        </div>
      </section>

      <Ergebnisse fehler={fehler} offline={offline} warnungen={ergebnis?.warnungen}>
        {ergebnis && (
          <>
            <Block label="Stoff" b={ergebnis.auswertung.stoff} />
            <Block label="Nähen" b={ergebnis.auswertung.naehen} />
            <Block label="Schiene" b={ergebnis.auswertung.schiene} />
            <Block label="Montage" b={ergebnis.auswertung.montage} />
            <Block label="Gesamt" b={ergebnis.auswertung.gesamt} betont />
          </>
        )}
      </Ergebnisse>

      {ergebnis && (
        <section className="kalk-abschnitt kalk-auswertung">
          <div className="kalk-auswertung__spalte">
            <h3>Je Dekometer</h3>
            <div className="kalk-kennzahlen kalk-kennzahlen--klein">
              <Kennzahl label="VK netto"><Zahl wert={ergebnis.auswertung.proDekometer.vkNetto} art="euro" /></Kennzahl>
              <Kennzahl label="Gewinn"><Zahl wert={ergebnis.auswertung.proDekometer.gewinnNetto} art="euro" /></Kennzahl>
              <Kennzahl label="Dekometer"><Zahl wert={ergebnis.auswertung.dekometerStoff} art="m" /></Kennzahl>
              <Kennzahl label="Stoffbedarf"><Zahl wert={ergebnis.auswertung.stoffbedarf.gesamtM} art="m" /></Kennzahl>
            </div>
            {ergebnis.auswertung.lohntSich && <LohntSich wert={ergebnis.auswertung.lohntSich.quotient} gewinn={ergebnis.auswertung.lohntSich.gewinnProStunde} soll={ergebnis.auswertung.lohntSich.sollProStunde} />}
          </div>
          <div className="kalk-auswertung__spalte">
            <h3>Je Apartment</h3>
            <Gruppen gruppen={ergebnis.auswertung.proApartment} />
            <h3>Je Zimmer</h3>
            <Gruppen gruppen={ergebnis.auswertung.proZimmer} />
          </div>
          <div className="kalk-auswertung__spalte">
            <h3>Nähauftrag</h3>
            <p className="muted small">{ergebnis.bestellformular.kopf.anzahlPositionen} Positionen, {ergebnis.bestellformular.kopf.geplanterStoffbedarfM.toLocaleString("de-DE")} m Stoff</p>
            <button type="button" className="button kalk-knopf--primaer" onPointerDown={() => setBlattOffen(true)} onClick={() => setBlattOffen(true)}>Nähauftrag anzeigen</button>
          </div>
        </section>
      )}

      <SchienenAuswahl
        offen={schieneFuer != null}
        onClose={() => setSchieneFuer(null)}
        dekolaengeCm={schieneFuer != null ? (() => { const s = eingabe.positionen[schieneFuer]?.schiene; return s ? s.nischenmassCm + s.ueberstandLinksCm + s.ueberstandRechtsCm : eingabe.positionen[schieneFuer]?.dekolaengeCm ?? 0; })() : 0}
        onWahl={(w) => schieneFuer != null && uebernehmeSchiene(schieneFuer, w)}
      />

      <Blatt offen={blattOffen} onClose={() => setBlattOffen(false)} titel={`Nähauftrag ${ergebnis?.bestellformular.kopf.kommission ?? ""}`}>
        {ergebnis && <Naehauftrag b={ergebnis.bestellformular} />}
      </Blatt>
    </div>
  );
}

function StoffKarte({ titel, art, stoff, vorlagen, onChange }: { titel: string; art: "raumhoch" | "bahnbreit"; stoff: StoffEingabe | null; vorlagen?: (StoffEingabe & { gruppe: string })[]; onChange: (s: StoffEingabe | null) => void }) {
  const s = stoff ?? { bezeichnung: "", ekProMeter: 0, margeProzent: 50, bahnbreiteCm: 300 };
  const z = s.zusatzleistungen ?? {};
  const [listeOffen, setListeOffen] = useState(false);
  const [quelle, setQuelle] = useState<StoffVorlage | null>(null);
  const raumhoch = art === "raumhoch";
  return (
    <div className="kalk-stoff">
      <div className="kalk-stoff__kopf">
        <h3>{titel}</h3>
        <div className="kalk-stoff__quellen">
        <button type="button" className="button kalk-stoff__liste" onClick={() => setListeOffen(true)}>Aus Stoffliste</button>
        <StoffAuswahl
          offen={listeOffen}
          onClose={() => setListeOffen(false)}
          hinweis={raumhoch ? "Für raumhoch eignen sich Ballenbreiten ab 260 cm." : "Für bahnbreit eignen sich Ballenbreiten bis 175 cm."}
          onWahl={(v, vorlage) => { setQuelle(vorlage); onChange({ ...v, zusatzleistungen: s.zusatzleistungen, futterstoff: s.futterstoff }); }}
        />
        {vorlagen && vorlagen.length > 0 && (
          <select className="kalk-stoff__vorlage" value="" onChange={(e) => { const v = vorlagen.find((x) => x.bezeichnung === e.target.value); if (v) { const { gruppe: _g, ...rest } = v; onChange({ ...rest, zusatzleistungen: s.zusatzleistungen }); } }} aria-label="Festpreisstoff übernehmen">
            <option value="">Festpreisstoff …</option>
            {vorlagen.map((v) => (
              <option key={v.bezeichnung ?? ""} value={v.bezeichnung ?? ""}>{v.gruppe} · {v.bezeichnung} · {v.ekProMeter.toLocaleString("de-DE")} €/m</option>
            ))}
          </select>
        )}
        </div>
      </div>
      {quelle && quelle.stoff.bezeichnung === s.bezeichnung && (
        <p className="kalk-stoff__quelle">Aus der Stoffliste: {quelle.herstellerName}{quelle.kollektion ? ` · ${quelle.kollektion}` : ""} · Faktor {quelle.faktor.toLocaleString("de-DE")}</p>
      )}
      <div className="kalk-stoff__felder">
        <TextFeld label="Bezeichnung" wert={s.bezeichnung ?? ""} onChange={(v) => onChange({ ...s, bezeichnung: v })} />
        <ZahlFeld label="EK je Meter" wert={s.ekProMeter} onChange={(v) => onChange({ ...s, ekProMeter: v })} einheit="€" min={0} schritt={0.1} />
        <ZahlFeld label="Marge" wert={s.margeProzent} onChange={(v) => onChange({ ...s, margeProzent: v })} einheit="%" min={0} schritt={5} />
        <ZahlFeld label="Bahnbreite" wert={s.bahnbreiteCm} onChange={(v) => onChange({ ...s, bahnbreiteCm: v })} einheit="cm" min={1} schritt={5} />
      </div>
      <div className="kalk-stoff__schalter">
        <Schalter label="Wave" an={z.wave ?? false} onChange={(an) => onChange({ ...s, zusatzleistungen: { ...z, wave: an, oesen: an ? false : z.oesen } })} />
        <Schalter label="Verd. Band" an={z.verdecktesVorhangband ?? false} onChange={(an) => onChange({ ...s, zusatzleistungen: { ...z, verdecktesVorhangband: an } })} />
        <Schalter label="Haken einh." an={z.hakenEinhaengen ?? false} onChange={(an) => onChange({ ...s, zusatzleistungen: { ...z, hakenEinhaengen: an } })} />
        <Schalter label="Futter" an={s.futterstoff != null} onChange={(an) => onChange({ ...s, futterstoff: an ? { ekProMeter: 3, bahnbreiteCm: s.bahnbreiteCm } : null })} />
      </div>
    </div>
  );
}

function Block({ label, b, betont }: { label: string; b: ObjektkalkulationErgebnis["auswertung"]["gesamt"]; betont?: boolean }) {
  return (
    <div className={`kalk-kennzahl kalk-block${betont ? " kalk-kennzahl--betont" : ""}`}>
      <span>{label}</span>
      <strong><Zahl wert={b.vkNetto} art="euro" /></strong>
      <small className="kalk-block__fuss">
        <span>EK <Zahl wert={b.ekNetto} art="euro" /></span>
        <span className={b.gewinnNetto >= 0 ? "kalk-gut" : "kalk-schlecht"}>Gewinn <Zahl wert={b.gewinnNetto} art="euro" /></span>
      </small>
    </div>
  );
}

function Gruppen({ gruppen }: { gruppen: { bezeichnung: string; positionen: number[]; vkNetto: number }[] }) {
  if (gruppen.length === 0) return <p className="muted small">Keine Zuordnung.</p>;
  return (
    <ul className="kalk-gruppen">
      {gruppen.map((g) => (
        <li key={g.bezeichnung}>
          <span>{g.bezeichnung} <small className="muted">Pos. {g.positionen.join(", ")}</small></span>
          <strong><Zahl wert={g.vkNetto} art="euro" /></strong>
        </li>
      ))}
    </ul>
  );
}

/** Balken „Lohnt sich der Auftrag“: Gewinn je Arbeitsstunde gegen das Soll. */
function LohntSich({ wert, gewinn, soll }: { wert: number; gewinn: number; soll: number }) {
  const anteil = useFeder(Math.min(1.5, Math.max(0, wert)) / 1.5, { response: 0.5 });
  const ton = wert >= 1 ? "gut" : wert >= 0.6 ? "warnung" : "schlecht";
  return (
    <div className="kalk-lohnt">
      <div className="kalk-lohnt__kopf">
        <span>Gewinn je Arbeitsstunde</span>
        <strong><Zahl wert={gewinn} art="euro" /> <span className="muted">/ Soll {soll} €</span></strong>
      </div>
      <div className="kalk-lohnt__spur" role="meter" aria-valuenow={Math.round(wert * 100)} aria-valuemin={0} aria-valuemax={150} aria-label="Quotient Ist zu Soll">
        <span className={`kalk-lohnt__balken kalk-lohnt__balken--${ton}`} style={{ transform: `scaleX(${anteil})` }} />
        <span className="kalk-lohnt__soll" style={{ left: `${(1 / 1.5) * 100}%` }} />
      </div>
    </div>
  );
}

function Naehauftrag({ b }: { b: ObjektkalkulationErgebnis["bestellformular"] }) {
  const k = b.kopf;
  return (
    <div className="kalk-naehauftrag">
      <dl className="kalk-kopfdaten">
        <div><dt>Firma</dt><dd>{k.firma}</dd></div>
        <div><dt>Kommission</dt><dd>{k.kommission ?? "–"}</dd></div>
        <div><dt>Auftragsdatum</dt><dd>{k.auftragsdatum}</dd></div>
        <div><dt>Liefertermin</dt><dd>{k.liefertermin}</dd></div>
        <div><dt>Versand</dt><dd>{k.versandart}, {k.verpackungsart}</dd></div>
        <div><dt>Stoffbedarf</dt><dd>{k.geplanterStoffbedarfM.toLocaleString("de-DE")} m</dd></div>
      </dl>
      <table className="kalk-tabelle kalk-tabelle--kompakt">
        <thead>
          <tr><th>Pos.</th><th>Zimmer</th><th className="num">Anz.</th><th className="num">Fertigbreite</th><th className="num">Höhe</th><th>Stoff</th><th className="num">Stoffbreite</th><th>Band</th><th className="num">Zugabe</th><th className="num">Saum</th><th>Bleiband</th><th>Futter</th><th>Einhängen</th></tr>
        </thead>
        <tbody>
          {b.positionen.map((p) => (
            <tr key={p.pos}>
              <td>{p.pos}</td><td>{p.zimmerbezeichnung ?? "–"}</td><td className="num">{p.anzahl}</td>
              <td className="num">{p.fertigbreiteCm != null ? `${p.fertigbreiteCm} cm` : `${p.fertigbreiteBahnen} Bahnen`}{p.bahnenGedreht ? ` (${p.bahnenGedreht} Bahnen gedreht)` : ""}</td>
              <td className="num">{p.fertighoeheCm} cm</td><td>{p.stoffbezeichnung ?? "–"}</td><td className="num">{p.stoffbreiteCm} cm</td><td>{p.band}</td>
              <td className="num">{p.zugabeCm} cm</td><td className="num">{p.saumabschlussCm ?? "–"}</td><td>{p.bleiband ? "ja" : "–"}</td><td>{p.hinterfuettern ? "ja" : "nein"}</td><td>{p.einhaengen ? "ja" : "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
